import React, { PropsWithChildren } from "react";
import LoadingSpinner from "../loader/LoadingSpinner";

export interface IInfiniteComponentBase {
  useComponentLevelFilter?: boolean;
}

export interface IInfiniteBase extends IInfiniteComponentBase {
  itemsPerPage?: number;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => Promise<any>;
  loadingIndicator?: React.ReactNode;
  renderIntersector?: (ref: (node?: Element | null | undefined) => void) => React.ReactNode;
}

interface IInfiniteScrollProps extends IInfiniteBase {
}

export default function InfiniteScroll(props: PropsWithChildren<IInfiniteScrollProps>) {

  const {
    itemsPerPage = 15,
    loadingIndicator,
    loadingMore,
    hasMore,
    children,
    onLoadMore,
    renderIntersector
  } = props;

  const [page, setPage] = React.useState<number>(1);

  const loadMoreLoop = React.useRef<NodeJS.Timeout | number | undefined>();
  const observer = React.useRef<IntersectionObserver | null>(null);
  const inViewRef = React.useRef<boolean>(false);
  const runningRef = React.useRef<boolean>(false);

  const clearTimer = () => {
    if (loadMoreLoop.current) {
      clearTimeout(loadMoreLoop.current);
      loadMoreLoop.current = undefined;
    }
  };

  const content = children ? (Array.isArray(children) ? children : [children]) : undefined;

  const itemCount = content?.length ?? 0;
  const currentIndex = page * itemsPerPage;

  const goToNextPage = () => {
    if (currentIndex > itemCount) return;
    setPage(prev => prev + 1);
  }

  const handleLoadMore = React.useCallback(async () => {
    if (currentIndex < itemCount) {
      goToNextPage();
      return;
    }

    if (loadingMore || !hasMore || !onLoadMore) {
      return;
    }

    await onLoadMore();
    goToNextPage();
  }, [currentIndex, itemCount, loadingMore, hasMore, onLoadMore]);

  const scheduleLoadMore = React.useCallback(async () => {

    if (!inViewRef.current || runningRef.current) {
      return;
    }

    runningRef.current = true;

    try {
      if (loadingMore) {
        clearTimer();
        loadMoreLoop.current = setTimeout(() => {
          void scheduleLoadMoreRef.current();
        }, 250);
        return;
      }

      await handleLoadMore();

      if (
        inViewRef.current &&
        (currentIndex < itemCount || hasMore)
      ) {
        clearTimer();
        loadMoreLoop.current = setTimeout(() => {
          void scheduleLoadMoreRef.current();
        }, 0);
      }
    } finally {
      runningRef.current = false;
    }

  }, [loadingMore, handleLoadMore, currentIndex, itemCount, hasMore]);

  const scheduleLoadMoreRef = React.useRef<() => Promise<void>>(async () => { });

  React.useEffect(() => {
    scheduleLoadMoreRef.current = scheduleLoadMore;
  }, [loadingMore, hasMore, inViewRef.current]);

  const setIntersectorRef = (node: Element | null | undefined) => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }

    if (!node) return;

    const o = new IntersectionObserver(async (entries) => {
      const entry = entries[0];

      inViewRef.current = !!entry?.isIntersecting;

      void scheduleLoadMoreRef.current();
    });

    o.observe(node);
    observer.current = o;

    const rect = node.getBoundingClientRect();

    const viewH = window.innerHeight;

    const inView = rect.top <= viewH && rect.bottom >= 0;

    if (inView) {
      inViewRef.current = true;
      void scheduleLoadMoreRef.current();
    }
  };

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(itemCount / itemsPerPage));
    if (page > maxPage) setPage(maxPage);
    if (itemCount === 0 && page !== 1) setPage(1);
  }, [itemCount, itemsPerPage, page]);

  React.useEffect(() => {
    return () => {
      observer.current?.disconnect();
      clearTimeout(loadMoreLoop.current);
    }
  }, []);

  const items = content?.slice(0, currentIndex) ?? [];

  return (
    <>
      {
        items
      }
      {
        (loadingMore && itemCount > itemsPerPage) && (
          loadingIndicator ?? <LoadingSpinner className="ps-2 pb-2 pe-2" />
        )
      }
      {
        renderIntersector
          ? renderIntersector(setIntersectorRef)
          : (
            <div ref={setIntersectorRef} style={{ height: "1px", width: "100%" }} />
          )
      }
    </>
  )
}